import {BASE_TRACK_API_URL} from '@constants/config'
import {apiRequestWithAuth} from '@helpers/query/apiRequestWithAuth'
import {OnShowError} from '@baseTypes/base'
import {
	IBodyAddMikrotik,
	IBodyUpdateMikrotik,
	IGhostDevice,
	IPropsDeleteMikrotik,
	IRouterStateResponse,
	ISetupTask,
	IWgAllocation,
	MikrotikResponse,
	RouterAction,
} from '@containers/equipment/mikrotik/types'

const BASE_URL_MIKROTIK = `${BASE_TRACK_API_URL}/v1/mikrotik`

export const getConnectedMikrotikApi = async (
	onError?: OnShowError
): Promise<IGhostDevice[]> => {
	const url = `${BASE_URL_MIKROTIK}/connected`
	return await apiRequestWithAuth<IGhostDevice[]>({
		url,
		options: {
			method: 'GET',
		},
		onError
	})
}

export const getMikrotikListApi = async (
	onError?: OnShowError
): Promise<MikrotikResponse> => {
	return await apiRequestWithAuth<MikrotikResponse>({
		url: BASE_URL_MIKROTIK,
		options: {
			method: 'GET',
		},
		onError
	})
}

export const getMikrotikWgAllocationApi = async (
	onError?: OnShowError
): Promise<IWgAllocation> => {
	const url = `${BASE_URL_MIKROTIK}/wg-allocation`
	return await apiRequestWithAuth<IWgAllocation>({
		url,
		options: {
			method: 'GET',
		},
		onError
	})
}

export const addMikrotikApi = async (
	body: IBodyAddMikrotik,
	onError?: OnShowError
) => {
	return await apiRequestWithAuth({
		url: BASE_URL_MIKROTIK,
		options: {
			method: 'POST',
			body: JSON.stringify(body),
			headers: {
				'content-type': 'application/json',
			}
		},
		onError
	})
}

export const updateMikrotikApi = async (
	{id, ...body}: IBodyUpdateMikrotik,
	onError?: OnShowError
) => {
	const url = `${BASE_URL_MIKROTIK}/${id}`
	return await apiRequestWithAuth({
		url,
		options: {
			method: 'PUT',
			body: JSON.stringify(body),
			headers: {
				'content-type': 'application/json',
			}
		},
		onError
	})
}

export const deleteMikrotikApi = async (
	{id}: IPropsDeleteMikrotik,
	onError?: OnShowError
) => {
	const url = `${BASE_URL_MIKROTIK}/${id}`
	return await apiRequestWithAuth({
		url,
		options: {
			method: 'DELETE',
		},
		onError
	})
}

const routerHeaders = (routerPassword: string) => ({
	'X-Router-Password': routerPassword,
	'content-type': 'application/json',
})

export const getRouterStateApi = async (
	{id, routerPassword}: {id: number, routerPassword: string}
): Promise<IRouterStateResponse> => {
	return await apiRequestWithAuth<IRouterStateResponse>({
		url: `${BASE_URL_MIKROTIK}/${id}/router-state`,
		options: {
			method: 'GET',
			headers: routerHeaders(routerPassword),
		},
	})
}

export const startRouterActionApi = async (
	{id, action, routerPassword, body}: {id: number, action: RouterAction, routerPassword: string, body?: object}
): Promise<{taskId: string}> => {
	return await apiRequestWithAuth<{taskId: string}>({
		url: `${BASE_URL_MIKROTIK}/${id}/${action}`,
		options: {
			method: 'POST',
			headers: routerHeaders(routerPassword),
			body: body ? JSON.stringify(body) : undefined,
		},
	})
}

export const getSetupTaskApi = async (
	{id, taskId, routerPassword}: {id: number, taskId: string, routerPassword: string}
): Promise<ISetupTask> => {
	return await apiRequestWithAuth<ISetupTask>({
		url: `${BASE_URL_MIKROTIK}/${id}/setup-tasks/${encodeURIComponent(taskId)}`,
		options: {
			method: 'GET',
			headers: routerHeaders(routerPassword),
		},
	})
}

export const execRouterCommandApi = async (
	{id, routerPassword, command, write}: {id: number, routerPassword: string, command: string, write: boolean}
): Promise<{output: string}> => {
	return await apiRequestWithAuth<{output: string}>({
		url: `${BASE_URL_MIKROTIK}/${id}/exec`,
		options: {
			method: 'POST',
			headers: routerHeaders(routerPassword),
			body: JSON.stringify({command, write}),
		},
	})
}
